import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import {
  isExperienceOfferMarkerType,
  isExplorePlaceMarkerType,
  MARKER_TYPE_ITEM,
  isMarkerType,
  isOfferMarkerType,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { MarkerType } from 'tripPlanner/utils/mapMarkerTransforms'
import type { LinkRect } from './PlaceHoverPreview/types'
import { HOVER_CLOSE_DELAY_MS } from './PlaceHoverPreview/constants'

export interface HoveredMarkerState {
  placeId?: string
  markerId?: string
  placeName: string
  index: number
  rect: LinkRect
  markerType?: MarkerType
  offerId?: string
  title?: string
  description?: string
  imageId?: string
  imageIds?: Array<string>
}

interface UseMarkerHoverOptions {
  onMarkerHover?: (markerId: string | undefined) => void
  enabled?: boolean
}

/**
 * Encapsulates marker hover state and the delayed-close lifecycle used by
 * the map popover previews.
 *
 * A centralized `setHoverState` keeps the local `hoveredMarker` (controls
 * which popover is shown) and the parent `onMarkerHover` callback (controls
 * the drawer card highlight) in sync.
 *
 * `clearHover` is exposed for external handlers (map move, marker click)
 * that need to dismiss the popover without going through the delayed-close
 * path.
 */
export function useMarkerHover({ onMarkerHover, enabled = true }: UseMarkerHoverOptions) {
  const intl = useIntl()
  const fallbackLabels = useMemo(() => ({
    experience: intl.formatMessage({
      id: 'aAZflHUZqYEt1NHXVllx',
      defaultMessage: 'Experience',
      description: 'Fallback place name shown in the map hover popover when an experience marker has no name',
    }),
    hotel: intl.formatMessage(productLabels.hotel),
    place: intl.formatMessage({
      id: 'yyTyEDfPOdjdmg1ZJ5Us',
      defaultMessage: 'Place',
      description: 'Fallback place name shown in the map hover popover when a generic place marker has no name',
    }),
  }), [intl])
  const [hoveredMarker, setHoveredMarker] = useState<HoveredMarkerState | null>(null)
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setHoverState = useCallback((marker: HoveredMarkerState | null) => {
    setHoveredMarker(marker)
    // Two lists key off this one id. A marker with no card to highlight clears instead of
    // setting an id that matches nothing.
    const id = isOfferMarkerType(marker?.markerType) ? marker?.offerId :
      isExplorePlaceMarkerType(marker?.markerType) ? marker?.placeId :
        undefined
    onMarkerHover?.(id)
  }, [onMarkerHover])

  const clearHoverTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = null
    }
  }, [])

  // Clear pending timeout on unmount.
  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current)
        hoverCloseTimeoutRef.current = null
      }
    }
  }, [])

  /** Immediately clear hover state and cancel any pending close timeout. */
  const clearHover = useCallback(() => {
    clearHoverTimeout()
    setHoverState(null)
  }, [clearHoverTimeout, setHoverState])

  useEffect(() => {
    if (!enabled || !hoveredMarker) return
    document.addEventListener('scroll', clearHover, true)
    window.addEventListener('resize', clearHover)
    return () => {
      document.removeEventListener('scroll', clearHover, true)
      window.removeEventListener('resize', clearHover)
    }
  }, [enabled, hoveredMarker, clearHover])

  const getHoveredMarkerFromElement = useCallback((element: HTMLDivElement): HoveredMarkerState | null => {
    const {
      placeId,
      placeName,
      index: markerIndex,
      markerType,
      markerId,
      offerId,
      title,
      description,
      imageId,
      imageIds,
    } = element.dataset

    if (!markerIndex) return null

    const domRect = element.getBoundingClientRect()
    const rect: LinkRect = {
      top: domRect.top,
      bottom: domRect.bottom,
      left: domRect.left,
      right: domRect.right,
      width: domRect.width,
      height: domRect.height,
    }
    const index = parseInt(markerIndex, 10)
    const isOfferMarker = isOfferMarkerType(markerType)
    const isExperienceMarker = isExperienceOfferMarkerType(markerType)
    const markerOfferId = offerId ?? markerId
    const parsedImageIds = imageIds
      ? (() => {
          try {
            const value = JSON.parse(imageIds)
            return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
          } catch {
            return []
          }
        })()
      : []

    if (isOfferMarker && markerOfferId) {
      return {
        markerId,
        placeName: placeName ?? (isExperienceMarker ? fallbackLabels.experience : fallbackLabels.hotel),
        index,
        markerType: isMarkerType(markerType) ? markerType : undefined,
        offerId: markerOfferId,
        title,
        description,
        imageId,
        imageIds: parsedImageIds,
        rect,
      }
    }

    if (markerType === MARKER_TYPE_ITEM) {
      return {
        markerId,
        placeId,
        placeName: placeName ?? fallbackLabels.place,
        index,
        markerType: MARKER_TYPE_ITEM,
        offerId: markerOfferId,
        title,
        description,
        imageId,
        imageIds: parsedImageIds,
        rect,
      }
    }

    if (!placeId) {
      return null
    }

    return {
      placeId,
      markerId,
      placeName: placeName ?? fallbackLabels.place,
      index,
      markerType: isMarkerType(markerType) ? markerType : undefined,
      title,
      description,
      imageId,
      imageIds: parsedImageIds,
      rect,
    }
  }, [fallbackLabels])

  const setHoveredMarkerFromElement = useCallback((element: HTMLDivElement) => {
    clearHoverTimeout()
    setHoverState(getHoveredMarkerFromElement(element))
  }, [clearHoverTimeout, getHoveredMarkerFromElement, setHoverState])

  const handleMarkerMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!enabled) return
    setHoveredMarkerFromElement(event.currentTarget)
  }, [enabled, setHoveredMarkerFromElement])

  const handleMarkerMouseLeave = useCallback(() => {
    if (!enabled) return
    clearHoverTimeout()
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoverState(null)
      hoverCloseTimeoutRef.current = null
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearHoverTimeout, enabled, setHoverState])

  const handlePopoverMouseEnter = useCallback(() => {
    clearHoverTimeout()
  }, [clearHoverTimeout])

  const handlePopoverClose = useCallback(() => {
    setHoverState(null)
  }, [setHoverState])

  return {
    hoveredMarker,
    clearHover,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverClose,
  } as const
}
